<template>
  <section class="timeshift-container page">
    <TimeShiftModal
      v-if="shouldShowTimeShiftModal"
      @shift="onTimeShifted" />
    <VideoExportModal
      v-if="shouldShowExportModal"
      :source="source" />
    <ChooseChannelModal
      v-if="isChangeChannelModalShowing && channels"
      :channels="channels"
      :onClose="onCloseChooseChannelModal"
      :currentChannel="source"
      :onSubmit="onChooseChannel" />
    <div class="schedule-container container">
      <div class="left-container">
        <div class="section-sources">
          <div class="source-info">
            <div class="source-content current-source">
              <div class="content-container">
                <h5 class="channel-text" v-if="isScheduleLoading && !hasChannel && !source"><i class="fas fa-video"></i> Loading...</h5>
                <h5 class="channel-text" v-else-if="hasChannel && source"><i class="fas fa-video"></i> {{source.name}}</h5>
                <h5 class="channel-text" v-else-if="!isScheduleLoading && !hasChannel">{{$t('capture.source.empty')}}</h5>
              </div>
              <div class="button-container">
                <button
                  v-if="hasChannel"
                  type="button"
                  class="btn source-button change-source-button"
                  @click.stop="onToggleSource">
                  {{$t('capture.source.change')}}
                </button>
              </div>
            </div>
          </div>
        </div>
        <ScheduleCalendar
          v-if="hasChannel"/>
      </div>
      <div
        v-if="hasChannel"
        class="right-container">
        <h1 class="md-title">{{$t('timeShift.schedule.table.title', { date: scheduleDate, interpolation: { escapeValue: false } })}}</h1>
        <md-table
          v-if="isScheduleLoaded"
          class="schedule-table"
          v-model="schedule"
          md-card
          :md-selected-value.sync="selectedPrograms">
          <md-table-toolbar slot="md-table-alternate-header" slot-scope="{ count }">
            <div class="md-toolbar-section-start long-text">{{$t('timeShift.schedule.table.selected', {count})}}</div>
            <div class="md-toolbar-section-start short-text">{{$t('timeShift.schedule.table.selected_short', {count})}}</div>
            <div class="md-toolbar-section-end">
              <md-button
                @click.stop="resetSelectedPrograms">
                {{$t('timeShift.schedule.table.button.clearSelection')}}
              </md-button>
              <md-button
                class="md-raised md-primary">
                <i class="fas fa-history"></i>
                <span class="long-text show-margin">{{$t('timeShift.schedule.table.button.shiftSelected')}}</span>
                <span class="short-text">{{$t('timeShift.schedule.table.button.shiftSelected_short')}}</span>
              </md-button>
            </div>
          </md-table-toolbar>
          <md-table-row
            @click.stop="onShiftTimeClicked(item)"
            slot="md-table-row"
            slot-scope="{ item }">
            <md-table-cell
              class="thumbnail-container"
              :md-label="$t('timeShift.schedule.table.thumbnail')">
              <span v-if="isProgramLive(item)" class="live-badge">
                {{$t('capture.timeline.isLive')}}
              </span>
              <div class="aspect-ratio-container">
                <img
                  class="thumbnail"
                  :src="getThumbnail(item)"
                  :alt="item.title">
              </div>
            </md-table-cell>
            <md-table-cell
              class="program-info-cell schedule-time"
              :md-label="$t('timeShift.schedule.table.programInfo')">
              <p class="program-title">
                {{item.title}} ({{getScheduleRunTime(item)}})
              </p>
              <div class='time-group-container accurate-time'>
                <div class="time-group">
                  <span class='title'>
                    {{$t('timeShift.accurateSchedule')}}
                  </span>
                  <span class="time"> {{getAccurateRunTime(item)}}</span>
                </div>
              </div>
              <div class='time-group-container shifted-time'>
                <div class="time-group">
                  <span class="title">
                    {{$t('timeShift.modifiedSchedule')}}
                  </span>
                  <span class='time'> {{getShiftedScheduleTime(item)}}</span>
                  <OffsetBadge :scheduleItem="item" />
                </div>
              </div>
              <div class="mobile-only actions-container">
                <md-button
                  class="btn-shift-time"
                  @click.stop="onShiftTimeClicked(item)">
                  <i class="fas fa-history"></i> <span class="text">{{$t('timeShift.schedule.table.button.shift')}}</span>
                </md-button>
                <md-button
                  class="btn-export-clip"
                  :disabled="!isProgramRewatchable(item)"
                  @click.stop="onExportClicked(item)">
                  <i class="fas fa-cut"></i> <span class="text"> {{$t('timeShift.export.button.title')}}</span>
                </md-button>
              </div>
            </md-table-cell>
            <md-table-cell
              class="actions-cell"
              md-label="">
              <md-button
                class="btn-shift-time"
                @click.stop="onShiftTimeClicked(item)">
                <i class="fas fa-history"></i> <span class="text">{{$t('timeShift.schedule.table.button.shift')}}</span>
              </md-button>
              <md-button
                class="btn-export-clip"
                :disabled="!isProgramRewatchable(item)"
                @click.stop="onExportClicked(item)">
                <i class="fas fa-cut"></i> <span class="text"> {{$t('timeShift.export.button.title')}}</span>
              </md-button>
            </md-table-cell>
          </md-table-row>
        </md-table>
        <div
          class="loading schedule-table"
          v-show="isScheduleLoading">
          <md-progress-spinner class="md-accent" md-mode="indeterminate" />
          <h3 class="loading-text">Loading...</h3>
        </div>
        <div
          class="empty schedule-table"
          v-show="isScheduleEmpty">
          <h3 class="loading-text">{{$t('timeShift.emptySchedule', { date: scheduleDate })}}</h3>
        </div>
        <div
          class="empty schedule-table"
          v-show="isScheduleError">
          <h3 class="loading-text">{{$t('response:serverError')}}</h3>
          <p class="error-message">{{error}}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import _ from 'lodash';

import { getScheduleItemsByDate } from '@/api/TimeShift';
import { loadChannelById } from '@/api/TimeMachine';
import {
  getScheduleRunTime,
  getAccurateRunTime,
  getShiftedScheduleTime,
  isProgramLive,
  isProgramRewatchable,
} from '@/utils/timeShift';

import ChooseChannelModal from '@/components/parts/channel/ChooseChannelModal.vue';
import TimeShiftModal from '@/components/parts/timeShift/TimeShiftModal.vue';
import ScheduleCalendar from '@/components/parts/timeShift/ScheduleCalendar.vue';

import defaultThumbnail from '@/assets/images/no-thumbnail-blue.png';
import VideoExportModal from '@/components/parts/timeShift/VideoExportModal.vue';
import OffsetBadge from '@/components/parts/timeShift/OffsetBadge.vue';

export default {
  components: {
    OffsetBadge,
    VideoExportModal,
    ChooseChannelModal,
    ScheduleCalendar,
    TimeShiftModal,
  },
  data() {
    return {
      source: null,
      defaultThumbnail,
      schedule: [],
      selectedPrograms: [],
      offset: 0,
      error: null,
      isLoading: true,
      tableHeader: 0,
      isChangeChannelModalShowing: false,
    };
  },
  computed: {
    ...mapState({
      channels: state => state.teamInformation.channels,
      scheduleDate: state => state.timeShift.scheduleDate,
    }),
    ...mapGetters([
      'shouldShowTimeShiftModal',
      'shouldShowExportModal',
    ]),
    scheduleYear() {
      return _.get(this.$route.params, 'scheduleYear', null);
    },
    scheduleMonth() {
      return _.get(this.$route.params, 'scheduleMonth', null);
    },
    scheduleDay() {
      return _.get(this.$route.params, 'scheduleDay', null);
    },
    isScheduleEmpty() {
      return !this.isLoading && !this.error && this.schedule.length === 0;
    },
    isScheduleLoading() {
      return this.isLoading && !this.error;
    },
    isScheduleLoaded() {
      return !this.isLoading && !this.error && this.schedule.length > 0;
    },
    isScheduleError() {
      return !this.isLoading && this.error;
    },
    hasChannel() {
      return this.channels && this.channels.length > 0;
    },
  },
  watch: {
    scheduleYear() {
      this.loadSchedule();
    },
    scheduleMonth() {
      this.loadSchedule();
    },
    scheduleDay() {
      this.loadSchedule();
    },
  },
  async created() {
    await this.$store.dispatch('loadChannels');
    const source = this.channels[0];
    this.source = source;
    if (source) {
      await this.loadSchedule();
    } else {
      this.isLoading = false;
    }
  },
  methods: {
    getScheduleRunTime,
    getAccurateRunTime,
    getShiftedScheduleTime,
    isProgramLive,
    isProgramRewatchable,
    async loadSchedule() {
      this.isLoading = true;
      let date;
      if (this.scheduleYear && this.scheduleMonth && this.scheduleDay) {
        date = `${this.scheduleYear}-${this.scheduleMonth}-${this.scheduleDay}`;
      } else {
        const now = new Date();
        const year = now.getFullYear();
        let currentDate = now.getDate();
        let currentMonth = now.getMonth() + 1;
        if (currentDate < 10) {
          currentDate = `0${currentDate}`;
        }
        if (currentMonth < 10) {
          currentMonth = `0${currentMonth}`;
        }
        date = `${year}-${currentMonth}-${currentDate}`;
      }
      this.$store.commit('setScheduleDate', date);
      this.$store.commit('setScheduleSource', this.source);
      try {
        const response = await getScheduleItemsByDate(this.source._id, date);
        this.schedule = response.data.items;
      } catch (error) {
        this.error = error;
      }
      this.isLoading = false;
    },
    onCloseChooseChannelModal() {
      this.isChangeChannelModalShowing = false;
    },
    onChooseChannel(selectedSource) {
      this.onSelectSource(selectedSource);
      this.onCloseChooseChannelModal();
    },
    async onSelectSource(selectedSource) {
      const response = await loadChannelById(selectedSource._id);
      const channelInfo = response.data;
      this.source = { ...this.selectedSource, ...channelInfo };
      await this.loadSchedule();
    },
    onShiftTimeClicked(program) {
      this.$store.dispatch('setCurrentTimeShiftProgram', program);
    },
    onExportClicked(program) {
      this.$store.dispatch('setCurrentExportProgram', program);
    },
    getThumbnail(program) {
      if (program.coverImage) {
        return program.coverImage;
      }
      return this.defaultThumbnail;
    },
    resetSelectedPrograms() {
      this.selectedPrograms = [];
    },
    onToggleSource() {
      this.isChangeChannelModalShowing = !this.isChangeChannelModalShowing;
    },
    onTimeShifted() {
      this.$store.dispatch('setCurrentTimeShiftProgram', null);
      this.loadSchedule();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/variables.scss';

.mobile-only {
  display: none;
}

.actions-container {
  margin: 0 0 0 -17px;
}

::v-deep .md-table-row {
  cursor: pointer;
}

::v-deep .time-group-container {
  .time-group {
    .title {
      width: 120px;
      display: inline-block;
    }
  }
  &.accurate-time {
    .title, .time {
      color: $dark-300;
    }
  }
}

::v-deep .md-table-cell {
  height: 100px;
}

.program-info-cell {
  ::v-deep .md-table-cell-container {
    padding-right: 4px;
  }
}

.actions-cell {
  ::v-deep .md-table-cell-container {
    padding-left: 4px;
  }
}

.header {
  .container {
    .md-field-year {
      flex: 0 0 10%;
      margin-right: $spacer;
    }
    .md-field-month {
      flex: 0 0 20%;
    }
  }
}

.short-text {
  display: none;
  margin-left: 4px;
}

.schedule-container {
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 40px 0;
  .left-container {
    flex: 0 0 28%;
    .section-sources {
      width: 100%;
      background: $primary-background;
      border-radius: 4px;
      padding: 20px;
      margin-bottom: 20px;
      .source-info {
        width: 100%;
        display: flex;
        flex-flow: column;
        justify-content: space-between;
        align-items: flex-start;
        .source-content {
          flex: 0 0 100%;
          .content-container {
            .channel-text {
              font-weight: 700;
              margin-bottom: 4px;
            }
            ::v-deep .form-control-custom {
              font-size: $font-size-base * 0.9;
              margin-bottom: 4px;
              background: #222;
              border: none;
              color: $body-color;
            }
          }
        }
      }
    }
  }
  .right-container {
    flex: 0 0 70%;
    .md-title {
      padding: 22px 22px 4px;
      margin: 0;
      background-color: var(--md-theme-default-background);
    }
    .thumbnail-container {
      width: 160px;
      ::v-deep .md-table-cell-container {
        padding: 2px 4px;
      }
      .aspect-ratio-container {
        position: relative;
        padding-top: 56.25%;
        width: 100%;
        .thumbnail {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }
      }
    }
    .program-title {
      font-weight: bold;
      margin: 4px 0 6px;
      font-size: $font-size-base;
    }
    .program-run-time {
      margin: 0 0 4px;
    }
    .not-modified {
      color: #a4a4a4;
    }
    .live-badge {
      background-color: #bd3f3f;
      padding: 2px 4px;
      margin-left: 4px;
      border-radius: 4px;
      font-weight: 300;
      font-size: 0.75em;
      position: absolute;
      z-index: 2;
      top: 11px;
      opacity: 0.85;
    }
    .loading, .empty {
      padding: 60px 0;
      height: 510px;
      background: $primary-background;
      justify-content: center;
      align-items: center;
      display: flex;
      flex-flow: column;
      .loading-text {
        margin: 0 0 0 14px;
      }
    }
    .empty {
      .loading-text {
        margin: 0;
      }
      .error-message {
        margin: 10px 0;
      }
    }
  }
  .schedule-table {
    ::v-deep .actions-cell {
      .md-table-cell-container {
        display: flex;
        flex-flow: row;
      }
    }
  }
}

.btn-shift-time {
  .text {
    margin-left: 4px;
  }
}

@media screen and (max-width: 1366px) {
  .schedule-container {
    max-width: 95%;
  }
}

@media screen and (max-width: 1280px) {
  .actions-cell {
    ::v-deep .md-table-cell-container {
      padding-right: 0;
    }
  }
  .schedule-container {
    .right-container {
      ::v-deep .md-table-content {
        tr {
          > th:first-child {
            opacity: 0;
          }
        }
      }
      .thumbnail-container {
        width: 122px;
      }
    }
  }
}

@media screen and (max-width: 1140px) {
  ::v-deep .time-group-container {
    .time-group {
      .title {
        width: 108px;
      }
      .shift-badge {
        display: block;
        width: fit-content;
        margin: 3px 0 0 0;
        &.hide {
          display: none;
        }
      }
    }
  }
}

@media screen and (max-width: 992px) {
  .schedule-container {
    .calendar-container {
      flex: 0 0 34%;
    }
    .schedule-table {
      flex: 0 0 65%;
      ::v-deep .md-table-cell-selection {
        width: 42px;
      }
      ::v-deep .thumbnail-container {
        width: 120px;
        .md-table-cell-container {
          padding: 10px 0;
        }
      }
      ::v-deep .program-info-cell {
        .md-table-cell-container {
          padding: 10px 0 10px 24px;
        }
      }
      ::v-deep .offset-cell {
        .md-table-cell-container {
          padding: 10px 0 10px 24px;
        }
      }
      ::v-deep .actions-cell {
        .md-table-cell-container {
          flex-flow: column;
          align-items: flex-start;
          padding: 10px 0;
          .md-button {
            margin: 0;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 846px) {
  .schedule-container {
    flex-flow: column;
    justify-content: flex-start;
    align-items: stretch;
    .calendar-container {
      flex: 0 0 100%;
    }
    .schedule-table {
      flex: 0 0 100%;
      margin-top: 0;
    }
    .right-container {
      .md-title {
        padding: 22px 22px 16px;
        margin: 20px 0 0;
      }
    }
  }
}

@media screen and (max-width: 640px) {
  .short-text {
    display: inline-block;
  }
  .long-text {
    display: none;
  }
}

@media screen and (max-width: 480px) {
  .mobile-only {
    display: block;
  }
  .schedule-container {
    .thumbnail-container, ::v-deep tr > th.md-table-head, .actions-cell {
      display: none;
    }
    .schedule-table {
      margin-top: 0;
    }
    .right-container {
      .md-title {
        padding: 22px 22px 16px;
        margin: 20px 0 0;
      }
    }
  }
}

@media screen and (max-width: 420px) {
}
</style>
